import React, { useEffect, useRef, useState } from "react";

type VideoEmbedProps = {
  videoId: string;
  title: string;
  caption?: string;
  playLabel?: string;
  posterFallbackUrl?: string;
  bordered?: boolean;
  centerPlayButton?: boolean;
};

export function VideoEmbed({ videoId, title, caption, playLabel, posterFallbackUrl, bordered = false, centerPlayButton = false }: VideoEmbedProps) {
  const [playedVideoId, setPlayedVideoId] = useState<string | null>(null);
  const [nativeVideoId, setNativeVideoId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isPlaying = playedVideoId === videoId;
  const isNativePlayer = nativeVideoId === videoId;

  useEffect(() => {
    setPlayedVideoId(null);
    setNativeVideoId(null);
    // iPhone does not reliably carry a poster tap into a newly loaded iframe.
    // Prepare the native Play control only when this video enters the viewport.
    if (!/iPhone/.test(navigator.userAgent) || !containerRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setNativeVideoId(videoId);
        observer.disconnect();
      }
    }, { threshold: 0.01 });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [videoId]);

  useEffect(() => {
    if (isPlaying) {
      iframeRef.current?.focus();
    }
  }, [isPlaying, videoId]);

  return (
    <figure className="mx-auto w-full max-w-[960px]">
      <div ref={containerRef} className={`relative aspect-video overflow-hidden rounded-xl bg-[#080F1A]${bordered ? " ring-1 ring-white/10" : ""}`}>
        {isPlaying || isNativePlayer ? (
          <iframe
            ref={iframeRef}
            className="h-full w-full border-0"
            src={isNativePlayer
              ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&playsinline=1`
              : `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`}
            title={title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            tabIndex={isNativePlayer ? 0 : -1}
          />
        ) : (
          <button
            type="button"
            className="group absolute inset-0 flex items-center justify-center overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#33CDD3] focus-visible:ring-offset-2 focus-visible:ring-offset-[#080F1A]"
            aria-label={playLabel || `Play ${title}`}
            onClick={() => {
              if (/iPhone/.test(navigator.userAgent)) setNativeVideoId(videoId);
              else setPlayedVideoId(videoId);
            }}
          >
            <img
              className="h-full w-full object-cover opacity-80 transition-opacity duration-200 group-hover:opacity-100"
              src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
              alt=""
              loading="lazy"
              onError={posterFallbackUrl ? event => {
                if (event.currentTarget.src !== posterFallbackUrl) event.currentTarget.src = posterFallbackUrl;
              } : undefined}
              onLoad={posterFallbackUrl ? event => {
                if (event.currentTarget.naturalWidth <= 120 && event.currentTarget.src !== posterFallbackUrl) event.currentTarget.src = posterFallbackUrl;
              } : undefined}
            />
            <span className="absolute inset-0 bg-gradient-to-t from-[#080F1A]/65 via-transparent to-[#080F1A]/10" aria-hidden="true" />
            <span className={`${centerPlayButton ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "relative"} inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#33CDD3] text-[#080F1A] shadow-lg transition-transform duration-200 group-hover:scale-105 group-active:scale-[0.97]`} aria-hidden="true">
              <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-current" focusable="false">
                <path d="M8 5.5v13l10-6.5-10-6.5Z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {caption ? <figcaption className="mt-3 text-sm leading-6 text-slate-400">{caption}</figcaption> : null}
    </figure>
  );
}
